import axios from 'axios';
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getNotifications, getUnreadNotifications, getInbox, getNotificationsPreferences, addNotificationsPreference, removeNotificationsPreference, setReadStatus, registerNotificationTokenForUser } from "../../api/notifications";

export const fetchNotifications = createAsyncThunk(
  "notifications/fetchNotifications", async (params, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const page = state.notifications.page;
      const source = axios.CancelToken.source()
      const { signal } = thunkAPI;
      signal.addEventListener('abort', () => {
        source.cancel()
      })
      return await getNotifications(page + 1, signal);
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: error.message  });
    }
  }
);

export const registerNotificationToken = createAsyncThunk(
  "notifications/registerNotificationToken", async (params, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const userId = state.user.user.id;
      const source = axios.CancelToken.source()

      const registrationToken = params;
      const { signal } = thunkAPI;
      signal.addEventListener('abort', () => {
        source.cancel()
      })
      return await registerNotificationTokenForUser({ userId, registrationToken }, signal);
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: error.message  });
    }
  }
);

export const fetchUnreadNotifications = createAsyncThunk(
  "notifications/fetchUnreadNotifications", async (params, thunkAPI) => {
    try {
      const source = axios.CancelToken.source()
      const { signal } = thunkAPI;
      signal.addEventListener('abort', () => {
        source.cancel()
      })
      const { _page: { totalPages }, _embedded: { messages } } = await getUnreadNotifications(1, signal);
      let arrayOfPromisses = [];
      for (let i = 2; i <= totalPages; i++) {
       arrayOfPromisses.push(getUnreadNotifications(i, signal));
      }
      const responses = await Promise.all(arrayOfPromisses)
      return responses.reduce((acc, response) => {
        return [
          ...acc,
          ...response._embedded.messages,
        ];
      }, messages);
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: error.message  });
    }
  }
)

export const fetchInbox = createAsyncThunk(
  "notifications/fetchInbox", async (limit, thunkAPI) => {
    try {
      const source = axios.CancelToken.source()
      const { signal } = thunkAPI;
      signal.addEventListener('abort', () => {
        source.cancel()
      })
      return await getInbox(signal)
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: error.message  });
    }
  }
);

export const setNotificationAsRead = createAsyncThunk(
  "notifications/setNotificationAsRead", async (params, thunkAPI) => {
    try {
      const source = axios.CancelToken.source()
      const { signal } = thunkAPI;
      signal.addEventListener('abort', () => {
        source.cancel()
      })
      return await setReadStatus(params, signal);
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: error.message  });
    }
  }
);

export const fetchNotificationsPreferences = createAsyncThunk(
  "notifications/getNotificationsPreferences", async (params, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const userId = state.user.user.id;
      const source = axios.CancelToken.source()
      const { signal } = thunkAPI;
      signal.addEventListener('abort', () => {
        source.cancel()
      })
      return await getNotificationsPreferences(userId, signal);
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: error.message  });
    }
  }
);

export const addNotificationsPref = createAsyncThunk(
  "notifications/addNotificationsPreference", async (params, thunkAPI) => {
    try {
      const source = axios.CancelToken.source()
      const { signal } = thunkAPI;
      signal.addEventListener('abort', () => {
        source.cancel()
      })
      return await addNotificationsPreference(params, signal);
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: error.message  });
    }
  }
);

export const removeNotificationsPref = createAsyncThunk(
  "notifications/removeNotificationsPreference", async (params, thunkAPI) => {
    try {
      const source = axios.CancelToken.source()
      const { signal } = thunkAPI;
      signal.addEventListener('abort', () => {
        source.cancel()
      })
      return await removeNotificationsPreference(params, signal);
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: error.message  });
    }
  }
);

export const notificationSlice = createSlice({
  name: 'notification',
  initialState: {
    data: [],
    page: 0,
    totalPages: 0,
    inbox: {},
    settings: {},
    loading: false,
    notificationsLoading: false,
    notificationsRequested: false,
  },
  reducers: {
    lowerNotificationsCounter: (state, { payload }) => {
      const newRead = parseInt(state.inbox.read) + 1;
      const newUnread = parseInt(state.inbox.unread) - 1;
      state.inbox = {
        read: newRead,
        unread: newUnread,
      };
    },
    addNotificationSetting: (state, { payload }) => {
      const newPreferences = {...state.settings};
      newPreferences.myPreferences[payload.channel] = newPreferences.myPreferences[payload.channel] || [];
      newPreferences.myPreferences[payload.channel].push({name: payload._embedded.context.name, id: payload.id});
      state.settings = newPreferences;
    },
    removeNotificationSetting: (state, { payload }) => {
      const newPreferences = {...state.settings};
      state.settings = {
        ...state.settings,
        myPreferences: {
          ...state.settings.myPreferences,
          [payload.channel]: newPreferences.myPreferences[payload.channel].filter(item => item.name !== payload.contextName),
        }
      }
    },
    browserNotificationsRequested: (state, action) => {
      state.notificationsRequested = true;
    },
    resetNotifications: (state, action) => {
      state.page = 0;
      state.totalPages = 0;
      state.data = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchNotifications.pending, (state) => {
      state.notificationsLoading = true;
    });
    builder.addCase(
      fetchNotifications.fulfilled, (state, { payload }) => {
        const oldData = state.data;
        if(state.page !== payload._page.number){
          let data = [
            ...oldData,
            ...payload._embedded.messages,
          ]
          state.data = data;
        }
        state.page = payload._page.number;
        state.totalPages = payload._page.totalPages;
        state.notificationsLoading = false;
      }
    );
    builder.addCase(
      fetchNotifications.rejected,(state, action) => {
        state.notifications = "error";
        state.notificationsLoading = false;
        state.error = action.error.message;
      }
    );
    builder.addCase(fetchInbox.pending, (state) => {
      state.inbox = {};
      state.loading = true;
    });
    builder.addCase(
      fetchInbox.fulfilled, (state, { payload  }) => {
        state.inbox = payload;
        state.loading = false;
      }
    );
    builder.addCase(
      fetchInbox.rejected,(state, action) => {
        state.notifications = "error";
        state.loading = false;
        state.error = action.error.message;
      }
    );
    builder.addCase(fetchNotificationsPreferences.pending, (state) => {
      state.settings = {};
      state.loading = true;
    });
    builder.addCase(
      fetchNotificationsPreferences.fulfilled, (state, { payload }) => {
        state.settings = payload;
        state.loading = false;
      }
    );
    builder.addCase(
      fetchNotificationsPreferences.rejected,(state, action) => {
        state.settings = {};
        state.loading = false;
        state.error = action.error.message;
      }
    );
  }
})

export const {
  lowerNotificationsCounter,
  addNotificationSetting,
  removeNotificationSetting,
  browserNotificationsRequested,
  resetNotifications,
} = notificationSlice.actions

export default notificationSlice.reducer
